(() => {
  const SELECTOR = '[data-pw-campaign-popup]';
  const isDesignMode = Boolean(window.Shopify && Shopify.designMode);

  const storageAvailable = () => {
    try {
      const key = '__pw_popup_test__';
      localStorage.setItem(key, '1');
      localStorage.removeItem(key);
      return true;
    } catch (_) {
      return false;
    }
  };

  const hasStorage = storageAvailable();

  const visiblyOpen = (node) => {
    if (!node) return false;

    const style = window.getComputedStyle(node);
    const rect = node.getBoundingClientRect();

    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      Number(style.opacity || 1) === 0
    ) {
      return false;
    }

    return rect.width > 1 && rect.height > 1;
  };

  const pageIsBusy = (popup) => {
    if (!isDesignMode && document.hidden) return true;

    const definiteOpen = [
      '#cart-drawer[open]',
      'cart-drawer[open]',
      'search-drawer[open]',
      'search-drawer[aria-hidden="false"]',
      '#custom-mobile-drawer.is-open',
      'dialog[open]'
    ];

    for (const selector of definiteOpen) {
      const nodes = Array.from(document.querySelectorAll(selector));

      if (nodes.some((node) => !popup.contains(node) && visiblyOpen(node))) {
        return true;
      }
    }

    /*
      MinCart can leave its container in the DOM when closed, so only treat
      it as busy if the popup itself has real visible dimensions.
    */
    const mincartNodes = Array.from(
      document.querySelectorAll('.mincart-popup, #mincart-popup-container')
    );

    if (mincartNodes.some((node) => !popup.contains(node) && visiblyOpen(node))) {
      return true;
    }

    /*
      Avoid stacking over another genuinely visible modal, but ignore our own.
    */
    const otherDialogs = Array.from(
      document.querySelectorAll('[role="dialog"][aria-modal="true"]')
    );

    return otherDialogs.some((node) => {
      if (popup.contains(node)) return false;
      return visiblyOpen(node);
    });
  };

  const init = (popup) => {
    if (!popup || popup.dataset.pwPopupInit === '1') return;

    popup.dataset.pwPopupInit = '1';

    const campaignId = (popup.dataset.campaignId || 'default').trim();
    const configuredDelay = Math.max(0, Number(popup.dataset.delay || 10)) * 1000;
    const delay = isDesignMode ? 600 : configuredDelay;
    const suppressDays = Math.max(1, Number(popup.dataset.suppressDays || 7));
    const storageKey = `pwCampaignPopup:${campaignId}`;

    let previousFocus = null;
    let retryCount = 0;
    let showTimer = null;
    let countdownTimer = null;

    const countdown = popup.querySelector('[data-pw-offer-countdown]');
    const endValue = (popup.dataset.offerEnd || '').trim();
    const endTime = Date.parse(endValue);

    const setCountdownValue = (selector, value) => {
      const node = popup.querySelector(selector);
      if (node) node.textContent = String(value).padStart(2, '0');
    };

    const updateCountdown = () => {
      if (!countdown) return;

      if (!endValue || !Number.isFinite(endTime)) {
        countdown.hidden = true;
        if (countdownTimer) window.clearInterval(countdownTimer);
        return;
      }

      const remaining = endTime - Date.now();

      if (remaining <= 0) {
        setCountdownValue('[data-pw-cd-days]', 0);
        setCountdownValue('[data-pw-cd-hours]', 0);
        setCountdownValue('[data-pw-cd-minutes]', 0);
        setCountdownValue('[data-pw-cd-seconds]', 0);
        if (countdownTimer) window.clearInterval(countdownTimer);
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      setCountdownValue('[data-pw-cd-days]', Math.floor(totalSeconds / 86400));
      setCountdownValue('[data-pw-cd-hours]', Math.floor((totalSeconds % 86400) / 3600));
      setCountdownValue('[data-pw-cd-minutes]', Math.floor((totalSeconds % 3600) / 60));
      setCountdownValue('[data-pw-cd-seconds]', totalSeconds % 60);
    };

    if (countdown) {
      updateCountdown();
      if (!countdown.hidden && Number.isFinite(endTime) && endTime > Date.now()) {
        countdownTimer = window.setInterval(updateCountdown, 1000);
      }
    }

    const suppressed = () => {
      /* Always allow previewing in Shopify Theme Editor. */
      if (isDesignMode || !hasStorage) return false;

      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;

      const until = Number(raw);

      if (!Number.isFinite(until)) {
        localStorage.removeItem(storageKey);
        return false;
      }

      if (Date.now() >= until) {
        localStorage.removeItem(storageKey);
        return false;
      }

      return true;
    };

    const suppress = () => {
      /* Theme Editor testing must never create a live dismissal memory. */
      if (isDesignMode || !hasStorage) return;

      const duration = suppressDays * 24 * 60 * 60 * 1000;
      localStorage.setItem(storageKey, String(Date.now() + duration));
    };

    const close = ({ remember = true } = {}) => {
      if (remember) suppress();

      popup.classList.remove('is-opening');
      popup.hidden = true;
      document.documentElement.classList.remove('pw-campaign-popup-open');

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
    };

    const open = () => {
      if (suppressed() || pageIsBusy(popup)) return false;

      previousFocus = document.activeElement;
      popup.hidden = false;
      document.documentElement.classList.add('pw-campaign-popup-open');

      requestAnimationFrame(() => {
        popup.classList.add('is-opening');
        popup.querySelector('.pw-campaign-popup__panel')?.focus({
          preventScroll: true
        });
      });

      return true;
    };

    const tryOpen = () => {
      if (suppressed()) return;

      if (pageIsBusy(popup)) {
        retryCount += 1;

        if (retryCount <= 20) {
          showTimer = window.setTimeout(tryOpen, 3000);
        }

        return;
      }

      open();
    };

    if (suppressed()) {
      popup.remove();
      return;
    }

    showTimer = window.setTimeout(tryOpen, delay);

    popup.querySelectorAll('[data-pw-popup-close]').forEach((control) => {
      control.addEventListener('click', () => close({ remember: true }));
    });

    popup.querySelector('[data-pw-popup-cta]')?.addEventListener('click', suppress);

    popup.querySelector('[data-pw-popup-copy]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const code = (button.dataset.code || '').trim();
      const status = button.querySelector('[data-pw-copy-status]');

      if (!code || !status) return;

      let copied = false;

      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch (_) {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();

        try {
          copied = document.execCommand('copy');
        } catch (_) {
          copied = false;
        }

        textarea.remove();
      }

      if (copied) {
        const original = status.textContent;
        status.textContent = 'COPIED â';

        window.setTimeout(() => {
          status.textContent = original;
        }, 1800);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (popup.hidden) return;

      if (event.key === 'Escape') {
        close({ remember: true });
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = Array.from(
        popup.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((node) => !node.hidden && node.offsetParent !== null);

      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(popup)) return;

      if (showTimer) window.clearTimeout(showTimer);
      if (countdownTimer) window.clearInterval(countdownTimer);
      document.documentElement.classList.remove('pw-campaign-popup-open');
    });
  };

  const boot = () => {
    document.querySelectorAll(SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.(SELECTOR).forEach(init);
  });
})();
